import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import { CartItem, DrugItem, PackType, ProposalItem } from 'src/api/types';
import { zustandProposalCartStorage } from 'src/utils/cookieHelper';
import useStore from './useStore';
import { GTAGPrefix } from './useGTAG';

export type PharmacyDataType = {
  drugList: CartItem[];
  pharmacyInfo: ProposalItem;
};

export type PharmacyItemType = {
  pharmacyItem: PharmacyDataType;
};

interface State {
  pharmacyBookingList: PharmacyItemType;
  bookingOrder: PharmacyDataType;
  proposalSelectTransition: boolean;
  setproposalSelectTransition: (state: boolean) => void;
  addNewPharmacyBooking: (data: {
    cartData: CartItem[];
    pharmacy: ProposalItem;
  }) => void;
  removePharmacyBooking: (pharmacy: ProposalItem) => void;
  setBookingOrder: (data: {
    drugList: CartItem[];
    pharmacyInfo: ProposalItem;
  }) => void;
  cleanBookingOrder: () => void;
  cleanPharmacyBookingList: () => void;
  removeItemFromBooking: (data: {
    pharmacy: ProposalItem;
    translitName: string;
  }) => void;
  addDrugToBookingCart: (data: {
    drugProps: {
      drug: DrugItem;
      packQuantity: number;
      unitQuantity: number;
      packType?: PackType;
      gtagPrefix?: GTAGPrefix;
      fbclid?: string;
      fbp?: string;
    };
    pharmacy: ProposalItem;
  }) => void;
}
const useProposalCartStore = create<State>()(
  persist(
    (set) => ({
      proposalSelectTransition: false,
      setproposalSelectTransition: (proposalSelectTransition) =>
        set({ proposalSelectTransition }),
      pharmacyBookingList: null,
      bookingOrder: null,
      addDrugToBookingCart: async (data: {
        drugProps: {
          drug: DrugItem;
          packQuantity: number;
          unitQuantity: number;
          packType?: PackType;
          gtagPrefix?: GTAGPrefix;
          fbclid?: string;
          fbp?: string;
        };
        pharmacy: ProposalItem;
      }) => {
        // TODO: fix types
        // eslint-disable-next-line
        // @ts-ignore
        set((state) => {
          const {
            drug,
            packQuantity,
            unitQuantity,
            packType,
            gtagPrefix,
            fbclid,
            fbp
          } = data.drugProps;
          const currentList = state?.pharmacyBookingList ?? [];
          const newList = { ...currentList };
          const currentPharmacy =
            newList[`${data.pharmacy?.branchCode}-${data.pharmacy?.partnerId}`];
          const existingDrug = currentPharmacy.drugList.find(
            (cartDrug) => cartDrug?.drug?.translitName === drug.translitName
          );
          if (existingDrug) {
            // Drug already in the cart
            // Changing quantity of the exisiting item
            existingDrug.packQuantity = packQuantity || 0;
            existingDrug.unitQuantity = unitQuantity || 0;
            existingDrug.packType = packType || PackType.PACK;
          } else {
            // Adding new item to drugList
            currentPharmacy.drugList.push({
              translitName: drug.translitName,
              packQuantity: packQuantity || 0,
              unitQuantity: unitQuantity || 0,
              packType: packType || PackType.PACK,
              quantityUnitInPack: drug.quantityUnitInPack,
              gtagPrefix: gtagPrefix || GTAGPrefix.default,
              name: drug.name,
              analytics: drug.analytics,
              fbclid,
              fbp
            });
          }
          return {
            pharmacyBookingList: newList
          };
        });
      },

      addNewPharmacyBooking: (data: {
        cartData: CartItem[];
        cartDrugsDetails?: DrugItem[];
        pharmacy: ProposalItem;
      }) => {
        set((state) => {
          const currentList = state?.pharmacyBookingList ?? null;
          const newList = { ...currentList };
          const currentPharmacy =
            newList[`${data.pharmacy?.branchCode}-${data.pharmacy?.partnerId}`];

          if (
            currentList &&
            Object.prototype.hasOwnProperty.call(
              currentList,
              `${data.pharmacy?.branchCode}-${data.pharmacy?.partnerId}`
            )
          ) {
            // pharmacy booking exist
            let newRests;
            data.cartData.forEach((cartItem) => {
              const existingDrug = currentPharmacy?.pharmacyInfo?.rests?.find(
                (restDrug) =>
                  restDrug?.drug?.translitName === cartItem.drug.translitName
              );
              if (!existingDrug) {
                currentPharmacy?.drugList.push(cartItem);
                newRests = currentPharmacy?.pharmacyInfo?.rests?.push(
                  data.pharmacy?.rests?.find(
                    (restDrug) =>
                      restDrug?.drug?.translitName ===
                      cartItem?.drug?.translitName
                  )
                );
              }
            });
            const newResetsList = {
              rests: Object.assign(
                currentPharmacy?.pharmacyInfo.rests,
                newRests
              )
            };
            const newItem = {
              [`${data.pharmacy?.branchCode}-${data.pharmacy?.partnerId}`]: {
                drugList: currentPharmacy?.drugList,
                pharmacyInfo: Object.assign(
                  currentPharmacy?.pharmacyInfo,
                  newResetsList
                )
              }
            };

            const newPharmacyList = Object.assign(newList, newItem);

            return {
              pharmacyBookingList: newPharmacyList
            };
          }

          // new pharmacy
          const newItem = {
            [`${data.pharmacy?.branchCode}-${data.pharmacy?.partnerId}`]: {
              drugList: data.cartData,
              pharmacyInfo: data.pharmacy
            }
          };

          const newPharmacyList = Object.assign(newList, newItem);

          return {
            pharmacyBookingList: newPharmacyList
          };
        });
      },
      removePharmacyBooking: (pharmacy: ProposalItem) => {
        set((state) => {
          const currentList = state?.pharmacyBookingList ?? null;
          const newList = { ...currentList };
          delete newList[`${pharmacy?.branchCode}-${pharmacy?.partnerId}`];
          return {
            pharmacyBookingList: newList
          };
        });
      },
      removeItemFromBooking: (data: {
        pharmacy: ProposalItem;
        translitName: string;
      }) =>
        set((state) => {
          const currentList = state?.pharmacyBookingList ?? null;
          const newList = { ...currentList };
          const currentPharmacy =
            newList[`${data.pharmacy?.branchCode}-${data.pharmacy?.partnerId}`];
          const newDrugItem = currentPharmacy.drugList.filter(
            (cartDrug) =>
              !data.translitName?.includes(cartDrug?.drug?.translitName)
          );
          const newPharmacyInfoDrugList =
            currentPharmacy.pharmacyInfo.rests.filter(
              (cartDrug) =>
                !data.translitName?.includes(cartDrug.drug.translitName)
            );
          currentPharmacy.drugList = newDrugItem;
          currentPharmacy.pharmacyInfo.rests = newPharmacyInfoDrugList;
          return { pharmacyBookingList: newList };
        }),

      setBookingOrder: (data: {
        drugList: CartItem[];
        pharmacyInfo: ProposalItem;
      }) => {
        set((state) => {
          const currentList = state?.bookingOrder ?? null;
          const newList = { ...currentList };
          const newItem = {
            drugList: data.drugList,
            pharmacyInfo: data.pharmacyInfo
          };
          const newBookingOrder = Object.assign(newList, newItem);

          return {
            bookingOrder: newBookingOrder
          };
        });
      },
      cleanBookingOrder: () => {
        set(() => {
          return {
            bookingOrder: null
          };
        });
      },
      cleanPharmacyBookingList: () => {
        set(() => {
          return {
            pharmacyBookingList: null
          };
        });
      }
    }),
    {
      name: zustandProposalCartStorage,
      storage: createJSONStorage(() => sessionStorage)
    }
  )
);

const fallbackStore: State = {
  proposalSelectTransition: null,
  setproposalSelectTransition: () => null,
  pharmacyBookingList: null,
  bookingOrder: null,
  addNewPharmacyBooking: () => null,
  removePharmacyBooking: () => null,
  removeItemFromBooking: () => null,
  addDrugToBookingCart: () => null,
  setBookingOrder: () => null,
  cleanBookingOrder: () => null,
  cleanPharmacyBookingList: () => null
};

export default () =>
  useStore<State>(useProposalCartStore, (state) => state) ?? fallbackStore;
